import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Search, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";

// Minimal, safe inline **bold** rendering (no HTML parsed, only text splits).
function renderAnswer(text) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="text-[#0F172A] font-semibold">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

export default function FAQPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("Toutes");

  useEffect(() => {
    api.get("/faq").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const s = new Set(items.map((i) => i.category));
    return ["Toutes", ...Array.from(s)];
  }, [items]);

  const filtered = items.filter((i) => {
    const matchQ = !q || i.question.toLowerCase().includes(q.toLowerCase()) || i.answer.toLowerCase().includes(q.toLowerCase());
    const matchC = cat === "Toutes" || i.category === cat;
    return matchQ && matchC;
  });

  // Group by category
  const grouped = filtered.reduce((acc, i) => {
    (acc[i.category] = acc[i.category] || []).push(i);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="faq-page">
      <div className="mb-10">
        <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">Besoin d'aide ?</span>
        <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold text-[#0F172A] tracking-tighter" style={{ fontFamily: "Outfit" }}>
          Foire aux questions<span className="text-[#D97706]">.</span>
        </h1>
        <p className="mt-3 text-[#475569] max-w-2xl">
          90% des questions des parents ont leur réponse ici. Utilisez la recherche pour trouver rapidement.
        </p>
      </div>

      <div className="relative mb-5">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#94A3B8]" />
        <Input
          placeholder="Rechercher une question (ex: inscription, cotisation, horaire...)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="faq-search"
          className="pl-12 h-14 text-base border-[#E2E8F0] focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            data-testid={`faq-cat-${c}`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              cat === c ? "bg-[#1E3A8A] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#1E3A8A]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 border border-dashed border-[#E2E8F0] rounded-lg">
          <HelpCircle className="h-10 w-10 mx-auto text-[#CBD5E1]" />
          <p className="mt-3 text-[#475569]">Aucune réponse trouvée. Essayez un autre mot-clé ou contactez-nous.</p>
          <Link to="/contact" className="mt-4 inline-block px-5 py-2.5 rounded-md bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors">
            Poser votre question
          </Link>
        </div>
      )}

      {Object.entries(grouped).map(([category, qs]) => (
        <div key={category} className="mb-8">
          <h2 className="text-xs tracking-[0.3em] uppercase font-bold text-[#D97706] mb-3 flex items-center gap-3">
            <span>{category}</span>
            <span className="h-px bg-[#E2E8F0] flex-1" />
          </h2>
          <Accordion type="single" collapsible className="space-y-2" data-testid={`faq-accordion-${category}`}>
            {qs.map((i) => (
              <AccordionItem
                key={i.id}
                value={i.id}
                className="border border-[#E2E8F0] rounded-lg bg-white px-5 data-[state=open]:border-[#1E3A8A]"
                data-testid={`faq-item-${i.id}`}
              >
                <AccordionTrigger className="text-left font-semibold text-[#0F172A] hover:text-[#1E3A8A] hover:no-underline py-5" style={{ fontFamily: "Outfit" }}>
                  {i.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#475569] leading-relaxed whitespace-pre-wrap pb-5">
                  {renderAnswer(i.answer)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      ))}

      <div className="mt-12 p-6 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] rounded-lg text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Pas trouvé ?</div>
          <p className="mt-1 text-slate-100">Posez votre question via le formulaire — on répond sous 48h.</p>
        </div>
        <Link to="/contact" data-testid="faq-contact-cta" className="px-6 py-3 rounded-md bg-white text-[#1E3A8A] font-semibold hover:bg-[#D97706] hover:text-white transition-colors">
          Contactez-nous
        </Link>
      </div>
    </div>
  );
}
